/**
 * Verify Email Page
 * Handles email verification token from URL.
 * After verification, auto-authenticates the user and redirects to the app.
 */

import { useAuth } from '@contexts/AuthContext';
import { Check, Loader2, X } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type VerificationStatus = 'verifying' | 'success' | 'error';

export function VerifyEmailPage(): ReactElement {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<VerificationStatus>(token ? 'verifying' : 'error');
  const [error, setError] = useState<string | null>(
    token ? null : 'No verification token provided',
  );

  useEffect(() => {
    if (!token) return;

    const verify = async (): Promise<void> => {
      try {
        await verifyEmail(token);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError((err as Error).message);
      }
    };

    verify();
  }, [token, verifyEmail]);

  const handleContinue = (): void => {
    navigate('/');
  };

  return (
    <div className="min-h-dvh bg-theme-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-theme-surface rounded-xl border border-theme-border p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-primary/15 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-theme-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Verifying Your Email</h1>
            <p className="text-theme-text-secondary">Please wait...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-success/15 flex items-center justify-center">
              <Check className="w-8 h-8 text-theme-success" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Email Verified</h1>
            <p className="text-theme-text-secondary mb-6">
              Your email has been verified. You're all set!
            </p>
            <button
              type="button"
              onClick={handleContinue}
              className="w-full bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Continue to App
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-theme-danger/15 flex items-center justify-center">
              <X className="w-8 h-8 text-theme-danger" />
            </div>
            <h1 className="text-2xl font-bold text-theme-text mb-2">Verification Failed</h1>
            <p className="text-theme-text-secondary mb-6">
              {error || 'The verification link is invalid or has expired.'}
            </p>
            <button
              type="button"
              onClick={handleContinue}
              className="w-full bg-theme-surface-hover hover:bg-theme-border text-theme-text font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
